import React from "react";
import DashboardNav from "../DashboardNavbar/DashboardNav";
import icon_people from "../../assets/img/icon_people.png";
import { getOrderUserwise } from "../../api/Api";
import dayjs from "dayjs";

import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  MenuItem,
  Avatar,
  Skeleton,
  TablePagination,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
function CustomerOrderHistory() {
  const location = useLocation();
  const rowData = location.state?.rowData;
  const navigate = useNavigate();
  const theme = useTheme();

  const [paymentStatus, setPaymentStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [orderHistory, setOrderHistory] = useState([]);
  const [monthlyOrderCount, setMonthlyOrderCount] = useState(0);
  const [payLaterCount, setPayLaterCount] = useState(0);
  const [paidOrderCount, setPaidOrderCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filteredOrders, setFilteredOrders] = useState([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    if (!rowData?._id) return;

    async function fetchOrderHistory() {
      setLoading(true); // Start loading
      try {
        const response = await getOrderUserwise(rowData._id);
        const orders = response?.data?.data || [];
        setOrderHistory(orders);
        setFilteredOrders(orders);

        const currentMonth = dayjs().month();
        let monthlyCount = 0;
        let payLaterOrderCount = 0;
        let paidOrderCount = 0;

        orders.forEach(({ placed_at, payment_type, payment_status }) => {
          const orderMonth = dayjs(placed_at).month();

          // Count orders placed in the current month
          if (orderMonth === currentMonth) {
            monthlyCount += 1;
          }

          // Count pay-later orders
          if (payment_type === "later") {
            payLaterOrderCount += 1;
          }

          // Count paid orders with "cod" payment type
          if (payment_status === "Paid") {
            paidOrderCount += 1;
          }
        });

        setMonthlyOrderCount(monthlyCount);
        setPayLaterCount(payLaterOrderCount);
        setPaidOrderCount(paidOrderCount);
      } catch (error) {
        console.error("Error fetching order data:", error);
        setOrderHistory([]);
        setFilteredOrders([]);
        setMonthlyOrderCount(0);
        setPayLaterCount(0);
        setPaidOrderCount(0);
      } finally {
        setLoading(false); // End loading
      }
    }

    fetchOrderHistory();
  }, [rowData]);

  // Handlers for the filter fields
  const handlePaymentStatusChange = (event) => {
    setPaymentStatus(event.target.value);
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  useEffect(() => {
    let filtered = [...orderHistory];

    if (paymentStatus) {
      filtered = filtered.filter(
        (order) => order.payment_status === paymentStatus
      );
    }

    if (fromDate) {
      filtered = filtered.filter((order) =>
        dayjs(order.created_at).isAfter(dayjs(fromDate), "day")
      );
    }

    if (toDate) {
      filtered = filtered.filter((order) =>
        dayjs(order.created_at).isBefore(dayjs(toDate), "day")
      );
    }

    setFilteredOrders(filtered);
  }, [paymentStatus, fromDate, toDate, orderHistory]);

  return (
    <Box sx={{ height: "100vh" }}>
      {/* Navbar */}
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <DashboardNav />
      </Box>
      {/* Main content */}
      <Box
        sx={{
          position: "absolute",
          top: "180px",
          left: 0,
          right: 0,
          px: 1,
          width: "90vw",
          margin: "auto",
          color: "#888585",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "500",
            fontSize: "32px",
            fontFamily: theme.typography.fontFamily,
            textTransform: "capitalize",
          }}
          gutterBottom
        >
          User Order History
        </Typography>

        {/* Order History Cards */}
        <Grid container spacing={1}>
          {/* monthly total order */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: "#E0FFF2",
                borderRadius: "10px",
                color: "#297643",
                padding: "20px",
                width: "80%",
                height: "80%",
                boxShadow: "0px 4px 4px 0px #9AEFB6",
                cursor: "pointer", // Add pointer cursor on hover
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "500",
                        marginBottom: "20px",
                        fontSize: "24px",
                        fontFamily: theme.typography.fontFamily,
                        textTransform: "capitalize", // Capitalizes each word
                      }}
                      gutterBottom
                    >
                      monthly total order
                    </Typography>

                    {/* Data */}
                    <Box display="flex" alignItems="center" gap={"2rem"}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "500",
                          fontSize: "24px",
                          color: "#888585",
                          marginRight: 1,
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        {monthlyOrderCount}
                      </Typography>
                      <Typography
                        color="#297643"
                        sx={{
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        +2.3%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Icon */}
                  <Box
                    sx={{
                      backgroundColor: "#36AB5D",
                      borderRadius: "50%",
                      width: "78px",
                      height: "80px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={icon_people}
                      alt=""
                      style={{
                        width: "70%",
                        height: "70%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* no of paylater orders */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: "#FC7F7F",
                borderRadius: "10px",
                color: "#843838",
                width: "80%",
                padding: "20px",
                boxShadow: "0px 4px 4px 0px #E59292",
                height: "80%",
                cursor: "pointer", // Add pointer cursor on hover
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "500",
                        marginBottom: "20px",
                        fontSize: "24px",
                        fontFamily: theme.typography.fontFamily,
                        textTransform: "capitalize", // Capitalizes each word
                      }}
                      gutterBottom
                    >
                      no of paylater orders
                    </Typography>

                    {/* Data */}
                    <Box display="flex" gap={"2rem"} alignItems="center">
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "500",
                          fontSize: "24px",
                          color: "#4D4C4C",
                          marginRight: 1,
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        {payLaterCount}
                      </Typography>
                      <Typography
                        color="#297643"
                        sx={{
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        +3.9%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Icon */}
                  <Box
                    sx={{
                      backgroundColor: "#843838",
                      borderRadius: "50%",
                      width: "78px",
                      height: "80px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={icon_people}
                      alt=""
                      style={{
                        width: "70%",
                        height: "70%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* no of paid orders */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: "#DFD8FF",
                borderRadius: "10px",
                color: "#3D2C8B",
                width: "80%",
                padding: "20px",
                boxShadow: "0px 4px 4px 0px #947FF6",
                height: "80%",
                cursor: "pointer", // Add pointer cursor on hover
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "500",
                        marginBottom: "20px",
                        fontSize: "24px",
                        fontFamily: theme.typography.fontFamily,
                        textTransform: "capitalize", // Capitalizes each word
                      }}
                      gutterBottom
                    >
                      no of paid orders
                    </Typography>

                    {/* Data */}
                    <Box display="flex" gap={"2rem"} alignItems="center">
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "500",
                          fontSize: "24px",
                          color: "#888585",
                          marginRight: 1,
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        {paidOrderCount}
                      </Typography>
                      <Typography
                        color="#297643"
                        sx={{
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        +1.3%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Icon */}
                  <Box
                    sx={{
                      backgroundColor: "#3D2C8B",
                      borderRadius: "50%",
                      width: "78px",
                      height: "80px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={icon_people}
                      alt=""
                      style={{
                        width: "70%",
                        height: "70%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* Customer Details */}
        <Box sx={{ mt: "10vh", color: "#888585" }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: "500",
              fontSize: "32px",
              fontFamily: theme.typography.fontFamily,
              textTransform: "capitalize",
            }}
            gutterBottom
          >
            Order Details
          </Typography>
          <Grid container spacing={4} sx={{ mb: 2 }} alignItems="center">
            {/* Payment Status Field */}
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Payment Status"
                variant="outlined"
                value={paymentStatus}
                onChange={handlePaymentStatusChange}
                select
              >
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Unpaid">Unpaid</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </TextField>
            </Grid>

            {/* From Date Field */}
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                value={fromDate}
                onChange={handleFromDateChange}
              />
            </Grid>

            {/* To Date Field */}
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                value={toDate}
                onChange={handleToDateChange}
              />
            </Grid>

            {/* "View Results" Button */}
            <Grid item xs={12} md={6} sx={{ textAlign: "right" }}>
              {/* <Button
                variant="contained"
                color="primary"
                onClick={handleViewResults}
                sx={{
                  height: "90%",
                  width: "20%",
                  padding: "13px",
                  marginRight: "10%",
                }}
              >
                View Results
              </Button> */}
            </Grid>
          </Grid>
        </Box>
        <TableContainer component={Paper} sx={{ mt: 8 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.primary.light }}>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Sl No
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Order Id
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  User Name
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Transaction Id
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Order Placed On
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Amount
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Payment Mode
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Payment Status
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Delivery Status
                </TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "16px" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from(new Array(10)).map((_, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton variant="text" width="100%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : [...filteredOrders]
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .reverse()
                    .map((row, index) => (
                      <TableRow key={row._id}>
                        <TableCell sx={{ fontSize: "16px" }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ fontSize: "16px" }}>
                          {row.user_id.slice(-4)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "16px" }}>
                          {row.user_name}
                        </TableCell>
                        <TableCell sx={{ fontSize: "16px" }}>
                          {row._id.slice(-4)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "16px" }}>
                          {dayjs(row.created_at).format("YYYY-MM-DD, h:mm A")}
                        </TableCell>
                        <TableCell sx={{ fontSize: "16px" }}>
                          {row.total_price}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "16px",
                            color:
                              row.payment_type === "later" ? "red" : "green",
                          }}
                        >
                          {row.payment_type}
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              row.payment_status === "Paid"
                                ? "#297643"
                                : "#EA4245",
                            fontSize: "16px",
                          }}
                        >
                          {row.payment_status}
                        </TableCell>
                        <TableCell
                          sx={{
                            color:
                              row.order_status === "Pending"
                                ? "#FF893A"
                                : row.order_status === "Delivered"
                                ? "#297643"
                                : "#EA4245",
                            fontSize: "16px",
                          }}
                        >
                          {row.order_status}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            disabled={row.payment_status !== "Paid"}
                            sx={{
                              color: "#ffffff",
                              borderColor: "#FF893A",
                              backgroundColor:
                                row.payment_status === "Paid"
                                  ? "#FF893A"
                                  : "#e0e0e0",
                              "&:hover": {
                                backgroundColor:
                                  row.payment_status === "Paid"
                                    ? "#43A047"
                                    : "#e0e0e0",
                              },
                              textTransform: "capitalize",
                            }}
                          >
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={orderHistory.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>
    </Box>
  );
}

export default CustomerOrderHistory;
